/**
 * NotificationService class
 * Handles notifications for clone and exam export processes
 */
export default class NotificationService {
    /**
     * @constructor
     * @param {Object} $injector - Angular $injector service
     * @param {Object} $timeout - Angular $timeout service
     * @param {Object} $q - Angular $q service
     * @param {Object} $http - Angular $http service
     */
    constructor($injector, $timeout, $q, $http) {
        if (!isCMS) {
            return false;
        }

        this._logIdentifier = 'NotificationService';
        this.$injector = $injector;
        this.$timeout = $timeout;
        this.$q = $q;
        this.$http = $http;
        this.$logger = $injector.get('LogBridgeService');
        this.$logger.info(this, 'initialize');

        this._Notification = null;
        this.cloneNotification = new this.CloneNotification();
        this.examExportNotification = new this.ExamExportNotification();
    }

    /**
     * Retrieves the Notification service
     * @returns {Object} Notification service
     */
    Notification() {
        if (!this._Notification) {
            this._Notification = this.$injector.get('Notification');
        }
        return this._Notification;
    }

    /**
     * CloneNotification inner class
     * Handles notifications for clone processes
     */
    CloneNotification = class {
        constructor() {
            this._cloneNotificationInProgress = false;
            this._cloneInProgress = [];
            this._onChangeCallBacks = [];
        }

        /**
         * Calls all registered onChange callbacks
         * @private
         */
        _callOnChangeCallBacks() {
            for (let callBackIndex = 0; callBackIndex < this._onChangeCallBacks.length; callBackIndex++) {
                let callBack = this._onChangeCallBacks[callBackIndex];
                if (callBack && angular.isFunction(callBack)) {
                    callBack();
                }
            }
        }

        /**
         * Fetches assessment clone status by type
         * @param {string} type - Status type to fetch
         * @returns {Promise} Promise object representing the HTTP request
         * @private
         */
        _getAssessmentCloneStatusByType(type = 'inprogress') {
            let defer = this.$q.defer();

            this.$http.get('/api/func/assessment_clone_status/' + type + '/').then((response) => {
                defer.resolve(response.data);
            });
            return defer.promise;
        }

        /**
         * Checks for clones in progress
         * @returns {Promise} Promise object representing the check
         * @private
         */
        _existsCloneInProgress() {
            let d = this.$q.defer();

            this._getAssessmentCloneStatusByType('inprogress').then((data) => {
                angular.forEach(data.objects, (value, key) => {
                    if (_.filter(this._cloneInProgress, {'id': value.id}).length === 0) {
                        this._cloneInProgress.push(value);
                        this.Notification()({
                            templateUrl: '/views/ib/notifications/cloneNotification/cloneInprogress.html',
                            title: value.assessment_origin__title,
                            closeOnClick: false,
                            delay: 5000
                        });

                        this._callOnChangeCallBacks();
                    }
                });
                if (this._cloneInProgress.length > 0) {
                    this._getCloneAssessmentNotInPogress().then(() => {
                        d.resolve();
                    });
                } else {
                    d.resolve();
                }
            });

            return d.promise;
        }

        /**
         * Processes clones not in progress
         * @returns {Promise} Promise object representing the processing
         * @private
         */
        _getCloneAssessmentNotInPogress() {
            let d = this.$q.defer();

            this._getAssessmentCloneStatusByType('not_inprogress').then((data) => {
                for (let index = this._cloneInProgress.length - 1; index >= 0; index--) {
                    let value = this._cloneInProgress[index];
                    let new_clone_obj = _.filter(data.objects, {'id': value.id});
                    if (new_clone_obj.length > 0) {
                        if (new_clone_obj[0].status === 'failed') {
                            this.Notification()({
                                templateUrl: '/views/ib/notifications/cloneNotification/cloneError.html',
                                message: new_clone_obj[0].status_msg,
                                title: new_clone_obj[0].assessment_origin__title,
                                closeOnClick: false,
                                delay: false
                            });
                        } else {
                            this.Notification()({
                                templateUrl: '/views/ib/notifications/cloneNotification/cloneCompleted.html',
                                title: new_clone_obj[0].assessment_origin__title,
                                closeOnClick: false,
                                delay: 5000
                            });
                        }

                        this._callOnChangeCallBacks();

                        if (index > -1) {
                            this._cloneInProgress.splice(index, 1);
                        }
                    }
                }
                d.resolve();
            });

            return d.promise;
        }

        /**
         * Initializes the clone notification process
         * @returns {boolean} False if already in progress, undefined otherwise
         * @private
         */
        _initCloneNotification() {
            if (this._cloneNotificationInProgress) return false;

            if (!this._cloneInProgress) {
                this._cloneInProgress = [];
            }
            this._cloneNotificationInProgress = true;

            this._existsCloneInProgress().then(() => {
                if (this._cloneInProgress.length > 0) {
                    this.$timeout(() => {
                        this._cloneNotificationInProgress = false;
                        this._initCloneNotification();
                    }, 2000, false);
                } else {
                    this._cloneNotificationInProgress = false;
                }
            });
        }

        /**
         * Adds a callback function to be called on changes
         * @param {Function} callBackFn - Callback function to add
         */
        addOnChangeCallBack(callBackFn) {
            if (callBackFn && angular.isFunction(callBackFn)) {
                this._onChangeCallBacks.push(callBackFn);
            }
        }

        /**
         * Removes a callback function from the onChange callbacks
         * @param {Function} callBackFn - Callback function to remove
         */
        removeOnChangeCallBack(callBackFn) {
            if (callBackFn && angular.isFunction(callBackFn)) {
                for (let callBackIndex = this._onChangeCallBacks.length - 1; callBackIndex >= 0; callBackIndex--) {
                    let callBack = this._onChangeCallBacks[callBackIndex];
                    if (_.isEqual(callBackFn, callBack)) {
                        if (callBackIndex > -1) {
                            this._onChangeCallBacks.splice(callBackIndex, 1);
                        }
                        break;
                    }
                }
            }
        }

        /**
         * Checks if a clone is in progress for a given UUID
         * @param {string} uuid - UUID to check
         * @returns {boolean} True if clone is in progress, false otherwise
         */
        isCloneInProgress(uuid) {
            return _.filter(this._cloneInProgress, {'assessment_origin__uuid': uuid}).length > 0;
        }

        /**
         * Initializes the CloneNotification instance
         */
        init() {
            this._initCloneNotification();
        }
    }

    /**
     * ExamExportNotification inner class
     * Handles notifications for exam export processes
     */
    ExamExportNotification = class {
        constructor() {
            this._examExportNotificationInProgress = false;
            this._examExportInProgress = [];
            this._onChangeCallBacks = [];
            this._firstInit = false;
        }

        /**
         * Calls all registered onChange callbacks
         * @private
         */
        _callOnChangeCallBacks() {
            for (let callBackIndex = 0; callBackIndex < this._onChangeCallBacks.length; callBackIndex++) {
                let callBack = this._onChangeCallBacks[callBackIndex];
                if (callBack && angular.isFunction(callBack)) {
                    callBack();
                }
            }
        }

        /**
         * Fetches exam export status by type
         * @param {string} type - Status type to fetch
         * @returns {Promise} Promise object representing the HTTP request
         * @private
         */
        _getExamExportStatusByType(type = 'inprogress') {
            let defer = this.$q.defer();

            this.$http.get('/cms/exam-export-status/' + type + '/').then((response) => {
                defer.resolve(response.data);
            });
            return defer.promise;
        }

        /**
         * Processes exam exports not in progress
         * @returns {Promise} Promise object representing the processing
         * @private
         */
        _getExamExportNotInPogress() {
            let d = this.$q.defer();

            this._getExamExportStatusByType('not_inprogress').then((data) => {
                for (let index = this._examExportInProgress.length - 1; index >= 0; index--) {
                    let value = this._examExportInProgress[index];
                    let new_exam_export_obj = _.filter(data.objects, {'id': value.id});
                    if (new_exam_export_obj.length > 0) {
                        if (new_exam_export_obj[0].status === 'failed') {
                            this.Notification()({
                                templateUrl: '/views/ib/notifications/examExportNotification/examExportError.html',
                                message: new_exam_export_obj[0].status_msg,
                                title: new_exam_export_obj[0].assessment__title,
                                closeOnClick: false,
                                delay: false
                            });
                        } else {
                            let notificationObject = this.Notification()({
                                templateUrl: '/views/ib/notifications/examExportNotification/examExportCompleted.html',
                                title: new_exam_export_obj[0].assessment__title,
                                message: String(new_exam_export_obj[0].id),
                                closeOnClick: false,
                                delay: false
                            });

                            notificationObject.then((notificationScope) => {
                                notificationScope.onDiscard = () => {
                                    notificationScope.kill();
                                    this.$http.delete('/cms/download-exam-export-pdf/' + String(value.id) + '/');
                                }
                            });
                        }

                        this._callOnChangeCallBacks();

                        if (index > -1) {
                            this._examExportInProgress.splice(index, 1);
                        }
                    }
                }
                d.resolve();
            });

            return d.promise;
        }

        /**
         * Checks for exam exports in progress
         * @returns {Promise} Promise object representing the check
         * @private
         */
        _existsExamExportInProgress() {
            let d = this.$q.defer();

            this._getExamExportStatusByType('inprogress').then((data) => {
                angular.forEach(data.objects, (value, key) => {
                    if (_.filter(this._examExportInProgress, {'id': value.id}).length === 0) {
                        this._examExportInProgress.push(value);
                        this.Notification()({
                            templateUrl: '/views/ib/notifications/examExportNotification/examExportInprogress.html',
                            title: value.assessment__title,
                            closeOnClick: false,
                            delay: 5000
                        });

                        this._callOnChangeCallBacks();
                    }
                });

                if (this._examExportInProgress.length > 0) {
                    this._getExamExportNotInPogress().then(() => {
                        d.resolve();
                    });
                } else {
                    d.resolve();
                }
            });

            return d.promise;
        }

        /**
         * Initializes the exam export notification process
         * @returns {boolean} False if already in progress, undefined otherwise
         * @private
         */
        _initExamExportNotification() {
            if (this._examExportNotificationInProgress) return false;

            if (!this._examExportInProgress) {
                this._examExportInProgress = [];
            }
            this._examExportNotificationInProgress = true;

            this._existsExamExportInProgress().then(() => {
                if (this._examExportInProgress.length > 0) {
                    this.$timeout(() => {
                        this._examExportNotificationInProgress = false;
                        this._initExamExportNotification();
                    }, 5000, false);
                } else {
                    this._examExportNotificationInProgress = false;
                }
            });
        }

        /**
         * Adds a callback function to be called on changes
         * @param {Function} callBackFn - Callback function to add
         */
        addOnChangeCallBack(callBackFn) {
            if (callBackFn && angular.isFunction(callBackFn)) {
                this._onChangeCallBacks.push(callBackFn);
            }
        }

        /**
         * Removes a callback function from the onChange callbacks
         * @param {Function} callBackFn - Callback function to remove
         */
        removeOnChangeCallBack(callBackFn) {
            if (callBackFn && angular.isFunction(callBackFn)) {
                for (let callBackIndex = this._onChangeCallBacks.length - 1; callBackIndex >= 0; callBackIndex--) {
                    let callBack = this._onChangeCallBacks[callBackIndex];
                    if (_.isEqual(callBackFn, callBack)) {
                        if (callBackIndex > -1) {
                            this._onChangeCallBacks.splice(callBackIndex, 1);
                        }
                        break;
                    }
                }
            }
        }

        /**
         * Checks if an exam export is in progress for a given UUID
         * @param {string} uuid - UUID to check
         * @returns {boolean} True if export is in progress, false otherwise
         */
        isExamExportInProgress(uuid) {
            return _.filter(this._examExportInProgress, {'assessment__uuid': uuid}).length > 0;
        }

        /**
         * Initializes the ExamExportNotification instance
         */
        init() {
            if (this._firstInit) {
                this._initExamExportNotification();
            } else {
                this._getExamExportStatusByType('completed').then((data) => {
                    this._firstInit = true;

                    angular.forEach(data.objects, (value, key) => {
                        let notificationObject = this.Notification()({
                            templateUrl: '/views/ib/notifications/examExportNotification/examExportCompleted.html',
                            title: value.assessment__title,
                            message: String(value.id),
                            closeOnClick: false,
                            delay: false
                        });
                        notificationObject.then((notificationScope) => {
                            notificationScope.onDiscard = () => {
                                notificationScope.kill();
                                this.$http.delete('/cms/download-exam-export-pdf/' + String(value.id) + '/');
                            }
                        });
                    });

                    this._initExamExportNotification();
                });
            }
        }
    }

    /**
     * Retrieves the CloneNotification instance
     * @returns {Object} CloneNotification instance
     */
    getCloneNotification() {
        return this.cloneNotification;
    }

    /**
     * Retrieves the ExamExportNotification instance
     * @returns {Object} ExamExportNotification instance
     */
    getExamExportNotification() {
        return this.examExportNotification;
    }

    /**
     * Initializes both CloneNotification and ExamExportNotification instances
     */
    init() {
        this.cloneNotification.init();
        this.examExportNotification.init();
    }
}